import { createMessageCiphertextEnvelope } from "@entzy/cnx-protocol-core";

const SHARED_CONVERSATION_KEY_BYTES = 32;
const AES_GCM_NONCE_BYTES = 12;

function readCrypto() {
  if (typeof window === "undefined") {
    throw new Error("Web Crypto is only available in the browser.");
  }

  const cryptoApi = window.crypto;

  if (!cryptoApi?.subtle || typeof cryptoApi.getRandomValues !== "function") {
    throw new Error("Web Crypto is not available in this browser.");
  }

  return cryptoApi;
}

function bytesToBase64(bytes) {
  let binary = "";

  bytes.forEach((value) => {
    binary += String.fromCharCode(value);
  });

  if (typeof window !== "undefined" && typeof window.btoa === "function") {
    return window.btoa(binary);
  }

  throw new Error("Base64 encoding is not available in this browser.");
}

function base64ToBytes(value) {
  if (typeof window === "undefined" || typeof window.atob !== "function") {
    throw new Error("Base64 decoding is not available in this browser.");
  }

  const binary = window.atob(String(value || "").trim());
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function bytesToHex(bytes) {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

async function importSharedConversationKey(conversationKeyBase64) {
  const keyBytes = base64ToBytes(conversationKeyBase64);

  if (keyBytes.length !== SHARED_CONVERSATION_KEY_BYTES) {
    throw new Error("Conversation key must decode to 32 bytes.");
  }

  return readCrypto().subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

async function sha256Hex(bytes) {
  const digest = await readCrypto().subtle.digest("SHA-256", bytes);
  return bytesToHex(new Uint8Array(digest));
}

export function createConversationKeyRef(conversationId) {
  return `conv-key:${String(conversationId || "").trim()}:v1`;
}

export async function generateSharedConversationKeyBase64() {
  const bytes = new Uint8Array(SHARED_CONVERSATION_KEY_BYTES);
  readCrypto().getRandomValues(bytes);
  return bytesToBase64(bytes);
}

export async function encryptConversationPlaintext({
  plaintext,
  conversationId,
  conversationKeyBase64,
  senderKeyRef,
  clientCreatedAt,
}) {
  const trimmedPlaintext = String(plaintext || "");

  if (!trimmedPlaintext) {
    throw new Error("Plaintext is required before encryption.");
  }

  const key = await importSharedConversationKey(conversationKeyBase64);
  const nonceBytes = new Uint8Array(AES_GCM_NONCE_BYTES);
  readCrypto().getRandomValues(nonceBytes);

  const plaintextBytes = new TextEncoder().encode(trimmedPlaintext);
  const ciphertextBuffer = await readCrypto().subtle.encrypt(
    {
      name: "AES-GCM",
      iv: nonceBytes,
    },
    key,
    plaintextBytes,
  );
  const ciphertextBytes = new Uint8Array(ciphertextBuffer);
  const ciphertextBase64 = bytesToBase64(ciphertextBytes);
  const nonceBase64 = bytesToBase64(nonceBytes);
  const digestHex = await sha256Hex(ciphertextBytes);

  return createMessageCiphertextEnvelope({
    ciphertext: ciphertextBase64,
    nonce: nonceBase64,
    content_kind: "text",
    conversation_key_ref: createConversationKeyRef(conversationId),
    ...(senderKeyRef ? { sender_key_ref: senderKeyRef } : {}),
    content_digest: `sha256:${digestHex}`,
    client_created_at: clientCreatedAt || new Date().toISOString(),
  });
}

export async function decryptConversationEnvelope({
  ciphertextEnvelope,
  conversationKeyBase64,
}) {
  const envelope =
    ciphertextEnvelope && typeof ciphertextEnvelope === "object"
      ? ciphertextEnvelope
      : {};
  const ciphertext = String(envelope.ciphertext || "").trim();
  const nonce = String(envelope.nonce || "").trim();

  if (!ciphertext || !nonce) {
    throw new Error("Ciphertext and nonce are required before decryption.");
  }

  const key = await importSharedConversationKey(conversationKeyBase64);
  const plaintextBuffer = await readCrypto().subtle.decrypt(
    {
      name: "AES-GCM",
      iv: base64ToBytes(nonce),
    },
    key,
    base64ToBytes(ciphertext),
  );

  return new TextDecoder().decode(plaintextBuffer);
}
